/**
 * autoBackup — silently snapshots a profile's full board to IndexedDB every
 * 24 hours of usage. The latest 7 snapshots are kept, oldest auto-evicted.
 *
 * This is fully local — no server roundtrip, no upload — so it works offline
 * and leaks zero data. Caregivers can recover their last good board state if
 * they accidentally wipe a category or change languages mid-day.
 */

const DB_NAME = "sonspeak_backups";
const STORE = "snapshots";
const MAX_SNAPSHOTS = 7;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function listSnapshots(profileId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.getAll();
    req.onsuccess = () =>
      resolve((req.result || []).filter((s) => s.profileId === profileId).sort((a, b) => b.ts - a.ts));
    req.onerror = () => reject(req.error);
  });
}

async function writeSnapshot(profileId, payload) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const doc = { profileId, ts: Date.now(), payload };
    const req = store.add(doc);
    req.onsuccess = async () => {
      // Evict oldest beyond MAX
      const existing = await listSnapshots(profileId);
      const overflow = existing.slice(MAX_SNAPSHOTS);
      if (overflow.length) {
        const tx2 = db.transaction(STORE, "readwrite");
        const s2 = tx2.objectStore(STORE);
        for (const o of overflow) {
          if (o.id != null) s2.delete(o.id);
        }
      }
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Call this whenever a board loads. Writes a snapshot only if the most recent
 * snapshot is older than 24 hours (or none exists). Cheap and safe to call on
 * every navigation.
 */
export async function maybeAutoBackup(profileId, getBoardSnapshot) {
  if (!("indexedDB" in window)) return; // no IndexedDB = no-op
  try {
    const recent = await listSnapshots(profileId);
    const latest = recent[0];
    if (latest && Date.now() - latest.ts < ONE_DAY_MS) return;
    const payload = await getBoardSnapshot();
    if (!payload) return;
    await writeSnapshot(profileId, payload);
  } catch {
    // Quotas, private mode, racing — silently skip. This is opportunistic.
  }
}

export async function getLastBackup(profileId) {
  try {
    const list = await listSnapshots(profileId);
    return list[0] || null;
  } catch {
    return null;
  }
}

export async function getAllBackups(profileId) {
  try {
    return await listSnapshots(profileId);
  } catch {
    return [];
  }
}
